import { GripVertical, X } from 'lucide-react';

interface NarrativeSection {
  id: string;
  title: string;
  body: string;
  locked?: boolean;
}

interface LookDeeperContent {
  narrativeSections: NarrativeSection[];
}

interface LookDeeperEditorEnhancedProps {
  content: LookDeeperContent;
  onChange: (content: LookDeeperContent) => void;
  disabled?: boolean;
  civicLevel?: string;
}

export default function LookDeeperEditorEnhanced({
  content,
  onChange,
  disabled = false,
  civicLevel = 'observer',
}: LookDeeperEditorEnhancedProps) {
  const isObserver = civicLevel.toLowerCase() === 'observer';

  // For Observer: Use only "What It's Really About" section
  const whatItsReallyAbout = isObserver
    ? content.narrativeSections.find(s => s.title === "What It's Really About") || {
        id: 'what-its-really-about',
        title: "What It's Really About",
        body: '',
        locked: true,
      }
    : null;

  // For Observer: Additional sections are all others
  // For other levels: All sections are treated as regular narrative sections
  const additionalSections = isObserver
    ? content.narrativeSections.filter(
        s => {
          // Exclude the fixed section by title
          if (s.title === "What It's Really About") {
            return false;
          }

          // Include sections that have either a title OR body content
          const hasTitle = s.title.trim().length > 0;
          const hasBody = s.body.trim().length > 0;

          return hasTitle || hasBody;
        }
      )
    : content.narrativeSections.filter(s => {
        const hasTitle = s.title.trim().length > 0;
        const hasBody = s.body.trim().length > 0;
        return hasTitle || hasBody;
      });

  const updateSection = (id: string, updates: Partial<NarrativeSection>) => {
    const updated = content.narrativeSections.map(s =>
      s.id === id ? { ...s, ...updates } : s
    );
    onChange({ ...content, narrativeSections: updated });
  };

  const addAdditionalSection = () => {
    const newSection: NarrativeSection = {
      id: `section-${Date.now()}`,
      title: '',
      body: '',
      locked: false,
    };
    onChange({
      ...content,
      narrativeSections: [...content.narrativeSections, newSection],
    });
  };

  const removeSection = (id: string) => {
    onChange({
      ...content,
      narrativeSections: content.narrativeSections.filter(s => s.id !== id),
    });
  };

  return (
    <div className="space-y-8">
      {/* Narrative Sections */}
      <div>
        <h3 className="text-[15px] font-semibold text-[#F5F5F5] mb-4">Narrative Sections</h3>

        {/* Observer Level: Show Fixed Section */}
        {isObserver && whatItsReallyAbout && (
          <div className="mb-3 bg-[#0A0A0A] border border-[#282828] rounded-lg p-4">
            <div className="mb-3">
              <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
                What It's Really About
              </label>
            </div>
            <textarea
              value={whatItsReallyAbout.body}
              onChange={(e) => updateSection(whatItsReallyAbout.id, { body: e.target.value })}
              disabled={disabled}
              rows={6}
              placeholder="Explain the deeper meaning and significance..."
              className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
            />
          </div>
        )}

        {/* Render sections differently based on civic level */}
        {additionalSections.length > 0 && (
          <div className={isObserver ? "mt-6" : ""}>
            {isObserver && <h4 className="text-[13px] font-medium text-[#A0A0A0] mb-3">Additional Sections</h4>}
            <div className="space-y-3">
              {additionalSections.map((section) => (
                <div
                  key={section.id}
                  className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4"
                >
                  {isObserver ? (
                    // Observer: Editable title + body (Additional Sections)
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <GripVertical className="w-4 h-4 text-[#606060] mt-1" />
                        {!disabled && (
                          <button
                            onClick={() => removeSection(section.id)}
                            className="p-1 hover:bg-[#282828] rounded transition-colors"
                          >
                            <X className="w-4 h-4 text-[#808080]" />
                          </button>
                        )}
                      </div>

                      <div className="space-y-3">
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                          disabled={disabled}
                          placeholder="Section title..."
                          className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
                        />
                        <textarea
                          value={section.body}
                          onChange={(e) => updateSection(section.id, { body: e.target.value })}
                          disabled={disabled}
                          rows={4}
                          placeholder="Section content..."
                          className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
                        />
                      </div>
                    </>
                  ) : (
                    // Other levels: Label + body (like fixed sections)
                    <>
                      <div className="mb-3">
                        <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
                          {section.title}
                        </label>
                      </div>
                      <textarea
                        value={section.body}
                        onChange={(e) => updateSection(section.id, { body: e.target.value })}
                        disabled={disabled}
                        rows={6}
                        placeholder="Write content for this section..."
                        className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export type { NarrativeSection, LookDeeperContent };
